import { Navigate } from 'react-router-dom';

import Authentication from '@/pages/auth/Authentication';
import Logout from '@/pages/auth/Logout';

import AnalyticsOverview from '@/pages/analytics/analytics-overview';
import AnalyticsBeta from '@/pages/analytics/analytics-beta';
import {
  ContractSyncPage,
  TableAnalyticsPage,
  TableDetailsPage,
  TableHandsPage,
  TablesPage,
  TableSyncPage,
  TableUpdatePage,
} from '@/pages/table/index';

import { UserAnalyticsPage, UserEditPage, UsersPage } from '@/pages/user/index';
import SignUpReportPage from '@/pages/users/signup-report';

import ROUTES from './routes-enums';

import RakeAnalytics from '@/pages/rake/rake-analytics';
import RakeTable from '@/pages/rake/rake-per-table';
import RakePlayer from '@/pages/rake/rake-per-player';

import Templates from '@/pages/template/templates';
import TemplateEdit from '@/pages/template/template-edit';
import TemplateCreate from '@/pages/template/template-create';

import BlindStructureCreate from '@/pages/template/blind-structure-create';

import PropPlayers from '@/pages/prop-players/prop-players';

import Transactions from '@/pages/transactions/transactions';
import GameStructure from '@/pages/template/mixed-game';
import Agreement from '@/pages/rake/agreements';
import AgreementsCreate from '@/pages/rake/agreements-create';
import BlindStructure from '@/pages/template/blind-structure';
import BlindStructureUpdate from '@/pages/template/blind-structure-update';
import RakeSharePayout from '@/pages/rake/rake-share-payouts';
import RakeSharePayoutPerPlayer from '@/pages/rake/rake-share-payouts-player';
import RewardsPayouts from '@/pages/rake/rewards-payouts';
import RewardsPayoutsPerPlayers from '@/pages/rake/rewards-payouts-player';
import RakeBackPayout from '@/pages/rake/rake-back-payouts';
import RakeBackPayoutPerPlayer from '@/pages/rake/rake-back-payouts-player';
import InternalTokensDeals from '@/pages/internal-tokens-deals/internal-tokens-deals';
import TokenFlowOrchestrator from '@/pages/token-flow-orchestrator/token-flow-orchestrator';

import ContractBalances from '@/pages/token/ContractBalances';

import BlindStructuresListPage from '@/pages/tournaments/BlindStructures/blind-structures-list-page';

import TournamentTemplatesPage from '@/pages/tournaments/TournamentTemplates/tournament-templates-page';
import TournamentListPage from '@/pages/tournaments/Tournaments/tournaments-page';
import TournamentDetailsPage from '@/pages/tournaments/TournamentDetails/tournament-details-page';
import TournamentRegistrationsPage from '@/pages/tournaments/TournamentRegistrations/tournament-registrations-page';
import TournamentOverviewPage from '@/pages/tournaments/TournamentsOverview/tournaments-overview-page';
import TournamentSettingsPage from '@/pages/tournaments/Settings/TournamentSettingsPage';
import TournamentSchedulePage from '@/pages/tournaments/schedule/TournamentSchedulePage';
import PayoutStructuresListPage from '@/pages/tournaments/PayoutStructures/payout-structures-list';
import CreatePayoutStructureForm from '@/pages/tournaments/PayoutStructures/create-payout-structure-form';
import EditPayoutStructureForm from '@/pages/tournaments/PayoutStructures/edit-payout-structure-form';

import SngTemplatesPage from '@/pages/tournaments/SnGTemplates/sng-templates-page';
import SngOverviewPage from '@/pages/tournaments/SnGOverview/tournaments-overview-page';
import SnGsPage from '@/pages/tournaments/SnGs/sngs-page';
import TournamentTicketsPage from '@/pages/tournaments/TournamentTickets/tournament-tickets-page';

import LeaderboardPayoutsPage from '@/pages/leaderboards/leaderboard-payouts-page';
import LeaderboardConfigPage from '@/pages/leaderboards/leaderboard-config-page';
import LeaderboardSchedulePage from '@/pages/leaderboards/leaderboard-schedule-page';

import { DepositDetailsPage, DepositsPage } from '@/pages/deposits/index';

import WalletTransfersPage from '@/pages/wallets/wallet-transfers-page';
import BulkTransfersPage from '@/pages/wallets/bulk-transfers-page';
import AlertsPage from '@/pages/alerts/AlertsPage';
import MaintenancePage from '@/pages/maintenance/MaintenancePage';
import FeatureFlagRoute from '@/components/FeatureFlagRoute/FeatureFlagRoute';
import SuperAdminRoute from '@/components/SuperAdminRoute/SuperAdminRoute';
import { FeatureFlags } from '@repo/common-types';
import {
  AgentDealUpdateRequestsPage,
  AgentMigrationsPage,
  AgentProfilePage,
  AgentsPage,
  CountriesPage,
  ReferralDepthFixPage,
  RegionsPage,
} from '@/pages/agents';
import DevToolsPage from '@/pages/dev-tools/DevToolsPage';

const authRoutes = [
  { path: ROUTES.AUTHENTICATION, component: <Authentication /> },
  { path: ROUTES.LOGOUT, component: <Logout /> },
];

const authProtectedRoutes = [
  // #region Analytics
  { path: ROUTES.ROOT, component: <AnalyticsOverview /> },
  {
    path: ROUTES.ANALYTICS_BETA,
    component: (
      <FeatureFlagRoute
        featureFlag={FeatureFlags.AnalyticsDashboard}
        component={<AnalyticsBeta />}
        redirectTo={ROUTES.ROOT}
      />
    ),
  },
  // #endregion

  // #region Tournaments
  { path: ROUTES.TOURNAMENT_BLIND_STRUCTURES, component: <BlindStructuresListPage /> },
  { path: ROUTES.TOURNAMENT_TEMPLATES, component: <TournamentTemplatesPage /> },
  { path: ROUTES.TOURNAMENTS_LIST, component: <TournamentListPage /> },
  { path: `${ROUTES.TOURNAMENT_DETAILS}/:tournamentId`, component: <TournamentDetailsPage /> },
  { path: `${ROUTES.TOURNAMENT_REGISTRATIONS}/:tournamentId`, component: <TournamentRegistrationsPage /> },
  { path: ROUTES.TOURNAMENTS_OVERVIEW, component: <TournamentOverviewPage /> },
  { path: ROUTES.TOURNAMENT_PAYOUT_STRUCTURES, component: <PayoutStructuresListPage /> },
  { path: ROUTES.TOURNAMENT_PAYOUT_STRUCTURE_DETAILS, component: <EditPayoutStructureForm /> },
  { path: ROUTES.TOURNAMENT_PAYOUT_STRUCTURE_CREATE, component: <CreatePayoutStructureForm /> },
  { path: ROUTES.SNG_OVERVIEW, component: <SngOverviewPage /> },
  { path: ROUTES.SNG_LIST, component: <SnGsPage /> },
  { path: ROUTES.SNG_TEMPLATES, component: <SngTemplatesPage /> },
  { path: ROUTES.TOURNAMENT_SETTINGS, component: <TournamentSettingsPage /> },
  { path: ROUTES.TOURNAMENT_SCHEDULES, component: <TournamentSchedulePage /> },
  { path: ROUTES.TOURNAMENT_TICKETS, component: <TournamentTicketsPage /> },
  // #endregion

  // #region Tables
  { path: ROUTES.TABLES, component: <TablesPage /> },
  { path: `${ROUTES.TABLE}/:tableId`, component: <TableDetailsPage /> },
  { path: ROUTES.TABLE_ANALYTICS, component: <TableAnalyticsPage /> },
  { path: ROUTES.TABLE_HANDS, component: <TableHandsPage /> },
  { path: `${ROUTES.TABLE_EDIT}/:tableId`, component: <TableUpdatePage /> },
  { path: `${ROUTES.TABLE_SYNC}`, component: <TableSyncPage /> },
  { path: `${ROUTES.CONTRACT_SYNC}`, component: <ContractSyncPage /> },
  { path: `${ROUTES.PARAMETERS_MIXED_GAMES_STRUCTURE}`, component: <GameStructure /> },
  { path: ROUTES.PARAMETERS_RAKE_STRUCTURE, component: <BlindStructure /> },
  { path: ROUTES.PARAMETERS_RAKE_STRUCTURE_CREATE, component: <BlindStructureCreate /> },
  { path: `${ROUTES.PARAMETERS_RAKE_STRUCTURE_UPDATE}/:rakeStructureId`, component: <BlindStructureUpdate /> },
  { path: ROUTES.TEMPLATES, component: <Templates /> },
  { path: ROUTES.TEMPLATE_CREATE, component: <TemplateCreate /> },
  { path: `${ROUTES.TEMPLATE}/:templateId`, component: <TemplateEdit /> },
  // #endregion

  // #region Users
  { path: ROUTES.USERS, component: <UsersPage /> },
  { path: `${ROUTES.USER_EDIT}/:userId`, component: <UserEditPage /> },
  { path: ROUTES.USER_ANALYTICS, component: <UserAnalyticsPage /> },
  { path: ROUTES.USER_DEALS, component: <InternalTokensDeals /> },
  { path: ROUTES.PROP_PLAYERS, component: <PropPlayers /> },
  { path: ROUTES.AGREEMENTS, component: <Agreement /> },
  { path: ROUTES.AGREEMENTS_CREATE, component: <AgreementsCreate /> },
  // #endregion

  // #region Agents
  // TODO: Add back when we develop the analytics page
  // {
  //   path: ROUTES.AGENTS_ANALYTICS,
  //   component: <FeatureFlagRoute featureFlag={FeatureFlags.CustomAgentDeals} component={<AgentAnalyticsPage />} />,
  // },
  {
    path: ROUTES.AGENTS,
    component: <FeatureFlagRoute featureFlag={FeatureFlags.CustomAgentDeals} component={<AgentsPage />} />,
  },
  {
    path: ROUTES.AGENT_PROFILE,
    component: (
      <FeatureFlagRoute
        featureFlag={FeatureFlags.CustomAgentDeals}
        component={<SuperAdminRoute component={<AgentProfilePage />} />}
      />
    ),
  },
  {
    path: ROUTES.AGENTS_MIGRATIONS,
    component: (
      <FeatureFlagRoute
        featureFlag={FeatureFlags.CustomAgentDeals}
        component={<SuperAdminRoute component={<AgentMigrationsPage />} />}
      />
    ),
  },
  {
    path: ROUTES.AGENTS_REFERRAL_DEPTH_FIX,
    component: (
      <FeatureFlagRoute
        featureFlag={FeatureFlags.CustomAgentDeals}
        component={<SuperAdminRoute component={<ReferralDepthFixPage />} />}
      />
    ),
  },
  {
    path: ROUTES.AGENTS_REGIONS,
    component: <FeatureFlagRoute featureFlag={FeatureFlags.CustomAgentDeals} component={<RegionsPage />} />,
  },
  {
    path: ROUTES.AGENTS_COUNTRIES,
    component: <FeatureFlagRoute featureFlag={FeatureFlags.CustomAgentDeals} component={<CountriesPage />} />,
  },
  {
    path: ROUTES.AGENTS_AGENT_DEAL_UPDATE_REQUESTS,
    component: (
      <FeatureFlagRoute featureFlag={FeatureFlags.CustomAgentDeals} component={<AgentDealUpdateRequestsPage />} />
    ),
  },
  // #endregion

  // #region Reports
  { path: ROUTES.REPORTS_SIGNUP, component: <SignUpReportPage /> },
  // #endregion

  // #region Rake
  { path: ROUTES.RAKE_ANALYTICS, component: <RakeAnalytics /> },
  { path: ROUTES.RAKE_PLAYER, component: <RakePlayer /> },
  { path: ROUTES.RAKE_TABLE, component: <RakeTable /> },
  { path: ROUTES.RAKE_SHARE, component: <RakeSharePayout /> },
  { path: `${ROUTES.RAKE_SHARE_PER_PLAYER}/:userId`, component: <RakeSharePayoutPerPlayer /> },
  { path: ROUTES.RAKE_BACK, component: <RakeBackPayout /> },
  { path: `${ROUTES.RAKE_BACK_PER_PLAYER}/:userId`, component: <RakeBackPayoutPerPlayer /> },
  { path: ROUTES.RAKE_REWARDS, component: <RewardsPayouts /> },
  { path: `${ROUTES.RAKE_REWARDS_PER_PLAYER}/:userId`, component: <RewardsPayoutsPerPlayers /> },
  // #endregion

  // #region Transactions
  { path: ROUTES.TRANSACTIONS, component: <Transactions /> },
  // #endregion

  // #region Tokens
  { path: `${ROUTES.TOKEN_FLOW_ORCHESTRATOR_WIZARD}`, component: <TokenFlowOrchestrator /> },
  { path: ROUTES.CONTRACT_BALANCES, component: <ContractBalances /> },
  // #endregion

  // #region Leaderboard
  { path: ROUTES.LEADERBOARD_PAYOUTS, component: <LeaderboardPayoutsPage /> },
  { path: ROUTES.LEADERBOARD_CONFIG, component: <LeaderboardConfigPage /> },
  { path: ROUTES.LEADERBOARD_SCHEDULE, component: <LeaderboardSchedulePage /> },
  // #endregion

  // #region Deposits
  { path: ROUTES.DEPOSITS, component: <DepositsPage /> },
  { path: ROUTES.DEPOSIT_DETAILS, component: <DepositDetailsPage /> },
  // #endregion

  // #region Wallets
  { path: ROUTES.WALLET_TRANSFERS, component: <WalletTransfersPage /> },
  { path: ROUTES.BULK_TRANSFERS, component: <BulkTransfersPage /> },
  // #endregion

  // #region Alerts
  { path: ROUTES.ALERTS, component: <AlertsPage /> },
  // #endregion

  // #region Maintenance
  { path: ROUTES.MAINTENANCE, component: <MaintenancePage /> },
  { path: ROUTES.DEV_TOOLS, component: <DevToolsPage /> },
  // #endregion

  // #region Fallback
  { path: '*', component: <Navigate to={ROUTES.TABLE_ANALYTICS} /> },
  // #endregion
];

const publicRoutes = [{ path: '*', component: <Navigate to={ROUTES.AUTHENTICATION} /> }];

export { publicRoutes, authRoutes, authProtectedRoutes };
